import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { Inline } from '../pvd-utils/enums/inline.js';
import { PvdCheckbox } from '../pvd-checkbox/pvd-checkbox.js';

const DEFAULT_ERROR = false;
const DEFAULT_INLINE = Inline.never;
const DEFAULT_REQUIRED = false;
const DEFAULT_TILES = false;
/**
 * Providence Checkbox Group component.
 * @see {@link https://providence.fmr.com/4.x/components/checkbox-group/checkbox-group.html|Checkbox Group documentation}
 */
class PvdCheckboxGroup extends PvdSlotifyBaseComponent {
    constructor() {
        super(...arguments);
        this.pvdError = DEFAULT_ERROR;
        this.pvdInline = DEFAULT_INLINE;
        this.pvdRequired = DEFAULT_REQUIRED;
        this.pvdTiles = DEFAULT_TILES;
        // #endregion
    }
    // #endregion
    // #region custom event listeners
    /**
     * Handle custom `select` event
     * @param event CustomEvent object containing data on the checkbox that fired the event
     */
    onPvdCheckboxChange(event) {
        // Only attempt to set select all if this checkbox group has a select all option
        if (this.selectAllCheckbox) {
            if (event.detail.isSelectAll) {
                this.handleSelectAll();
            }
            this.toggleSelectAllCheckbox();
        }
    }
    // #endregion
    // #region internal methods
    /**
     * Determine if a PvdCheckbox is checked
     * @param checkbox PvdCheckbox
     * @returns boolean true if PvdCheckbox is checked
     */
    isChecked(checkbox) {
        return checkbox.pvdChecked;
    }
    /**
     * Determine if all the checkboxes (excluding select all checkbox) are checked
     * @returns boolean true if all checkboxes (excluding select all checkbox) in this checkbox group are checked
     */
    get hasAllChecked() {
        return Array.from(this.checkboxes).every(this.isChecked);
    }
    /**
     * Determine if at least one checkbox (excluding select all checkbox) is checked
     * @returns boolean true if at least one checkbox (exluding select all checkbox) in this checkbox group is checked
     */
    get hasSomeChecked() {
        return Array.from(this.checkboxes).some(this.isChecked);
    }
    /**
     * Toggle select all checkbox value between checked, indeterminate, and unchecked
     */
    toggleSelectAllCheckbox() {
        this.selectAllCheckbox.pvdChecked = false;
        this.selectAllCheckbox.pvdIndeterminate = false;
        if (this.hasAllChecked) {
            this.selectAllCheckbox.pvdChecked = true;
        }
        else if (this.hasSomeChecked) {
            this.selectAllCheckbox.pvdIndeterminate = true;
        }
    }
    /**
     * Update the `pvdChecked` property of all inputs in this checkbox group
     */
    handleSelectAll() {
        this.checkboxes.forEach((checkbox, index) => {
            const oldCheckedValue = checkbox.pvdChecked;
            checkbox.pvdChecked = this.selectAllCheckbox.pvdChecked;
            checkbox.requestUpdate('pvdChecked', oldCheckedValue);
        });
    }
    // #endregion
    // #region public methods
    /**
     * Focus on the internal element with role="group" that wraps slotted Checkbox
     * components.
     *
     * @remarks
     * This method should be invoked whenever the Checkbox Group enters an error
     * state in order to give focus to the internal element with role="group".
     *
     * @param options - options that are passed through unmodified to the native
     * `focus` method
     */
    focus(options) {
        const checkboxGroupEl = this.querySelector('.pvd-checkbox-group__checkbox-group');
        if (checkboxGroupEl) {
            checkboxGroupEl.focus(options);
        }
    }
    // #endregion
    // #region LitElement lifecycles
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-checkbox-group-root`,
            modifierPrefix: 'pvd-checkbox-group',
            modifiers: [
                {
                    condition: this.pvdError,
                    className: 'error',
                },
                {
                    condition: this.pvdInline !== Inline.never,
                    className: `inline-${this.pvdInline}`,
                },
                {
                    condition: this.pvdTiles,
                    className: 'tiles',
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}"><fieldset @pvd-checkbox-change="${this.onPvdCheckboxChange}" aria-describedby="${l(this.pvdAriaDescribedby)}" aria-invalid="${l(this.a11yBoolean(this.pvdError))}" aria-label="${l(this.pvdAriaLabel)}" aria-labelledby="${l(this.pvdAriaLabelledby)}" aria-required="${l(this.a11yBoolean(this.pvdRequired))}" class="pvd-checkbox-group__checkbox-group" tabindex="-1"><s-slot></s-slot></fieldset></div>`;
    }
    firstUpdated() {
        super.firstUpdated();
        const namespace = PvdCheckbox.namespace;
        this.selectAllCheckbox = this.querySelector(`${namespace}-checkbox[pvd-select-all]`);
        this.checkboxes = Array.from(this.querySelectorAll(`${namespace}-checkbox:not(${namespace}-checkbox[pvd-select-all])`));
    }
}
//#region Component configuration
PvdCheckboxGroup.componentName = 'checkbox-group';
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-describedby' }),
    __metadata("design:type", String)
], PvdCheckboxGroup.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-label' }),
    __metadata("design:type", String)
], PvdCheckboxGroup.prototype, "pvdAriaLabel", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-labelledby' }),
    __metadata("design:type", String)
], PvdCheckboxGroup.prototype, "pvdAriaLabelledby", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-error',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-error'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckboxGroup.prototype, "pvdError", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-inline' }),
    __metadata("design:type", String)
], PvdCheckboxGroup.prototype, "pvdInline", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-required',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-required'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckboxGroup.prototype, "pvdRequired", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-tiles',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-tiles'),
    }),
    __metadata("design:type", Boolean)
], PvdCheckboxGroup.prototype, "pvdTiles", void 0);

export { DEFAULT_ERROR, DEFAULT_INLINE, DEFAULT_REQUIRED, DEFAULT_TILES, PvdCheckboxGroup };
//# sourceMappingURL=pvd-checkbox-group.js.map
